import { configuredChargeCurrent } from "../charge-current";
import { isAcOnlyComponent, isAcTerminalId } from "../ac";
import { isFuseBlockLike, isFuseHolderVariant } from "../auto-fuse";
import { computeBatterySourceRanges } from "../battery-stack";
import {
  getEffectiveCableFuse,
  isShuntPassthrough,
  type SimulateContext,
} from "../cable-current";
import type { DefLookup } from "../catalog";
import { dcChargingPortGroups, dcPortTerminalGroups, internalDcConnections, oneWayDcConnections, pvInputTerminalGroups } from "../component-ports";
export { dcSideTerminals } from "../component-ports";
import { indexNodesById } from "../node-tree";
import { effectiveTerminals } from "../effective";
import { isSenseTerminal } from "../sense-terminal";
import { dcBreakerSpec } from "../dc-breaker";
import {
  getEffectiveStateOption,
  getStaticIntraLinkOption,
  applyStateToAmp,
  hasEngineShutdownDetection,
  type SimulateStateOption,
} from "../simulate/states";
import type {
  Cable,
  ComponentDefinition,
  ComponentNode,
  System,
  SystemNode,
  Terminal,
} from "../types";

/** A graph vertex is "<nodeId>:<terminalId>". */
export type Vertex = string;

export const v = (nodeId: string, terminalId: string): Vertex =>
  `${nodeId}:${terminalId}`;

export const splitV = (vertex: Vertex): { nodeId: string; terminalId: string } => {
  const [nodeId, terminalId] = vertex.split(":");
  return { nodeId, terminalId };
};

export type IntraLinkKind = "fused-slot" | "dc-breaker" | "bus" | "passthrough" | "permanent";

export type IntraLink = {
  nodeId: string;
  from: string;
  to: string;
  kind: IntraLinkKind;
  /** On a "fused-slot" link, the rating in amps; undefined when unset. */
  fuseRating?: number;
  /** Installed breaker rating; never exported or displayed as a fuse. */
  breakerRating?: number;
};

/**
 * `cables` supplies fuse-block slot ratings: a slot's rating lives on the cable
 * connected to it, not on the node. Pass `[]` when only the shape matters.
 * `activeOption.intraLinks` replaces polarity grouping outright, rather than
 * adding to it.
 */
export function intraLinks(
  node: ComponentNode,
  def: ComponentDefinition,
  cables: readonly Cable[] = [],
  activeOption?: SimulateStateOption,
): IntraLink[] {
  const isolated = new Set(oneWayDcConnections(def).flat());
  const permanent: IntraLink[] = internalDcConnections(def)
    .filter(([from, to]) => !isolated.has(from) && !isolated.has(to))
    .map(([from, to]) => ({
    nodeId: node.id, from, to, kind: "permanent",
  }));
  // A shunt ties BATT−/LOAD− together, so the BFS reaches loads past it.
  if (def.category !== "distribution" && !isShuntPassthrough(def)) return permanent;
  activeOption ??= getStaticIntraLinkOption(def);

  const breaker = dcBreakerSpec(def);
  if (breaker) {
    const { inputTerminalId: from, outputTerminalId: to } = breaker;
    const closed = activeOption?.ampOverride !== 0 && (!activeOption?.intraLinks ||
      activeOption.intraLinks.some(([a, b]) => (a === from && b === to) || (a === to && b === from)));
    return closed ? [{ nodeId: node.id, from, to, kind: "dc-breaker", breakerRating: breaker.ratingA }] : [];
  }

  // Effective terminals, not `def.terminals`: a grown busbar's `stud-N` ids
  // exist only here, and loads on those studs would be flagged as floating.
  // An isolator's semiconductor paths are directional transfers, never bus
  // copper. In particular, two output banks must not merge through ALT. Keep
  // any independently declared common-negative/control wiring intact.
  const allTerminals = effectiveTerminals(def, node).filter(t => !isolated.has(t.id));

  // Only the listed pairs conduct; an endpoint the instance doesn't have is
  // dropped, so a catalog typo can't mint a phantom terminal.
  if (activeOption?.intraLinks && activeOption.ampOverride !== 0) {
    const termIds = new Set(allTerminals.map((t) => t.id));
    const out: IntraLink[] = [];
    for (const [a, b] of activeOption.intraLinks) {
      if (!termIds.has(a) || !termIds.has(b)) continue;
      out.push({ nodeId: node.id, from: a, to: b, kind: "bus" });
    }
    return [...permanent, ...out];
  }

  if (isAcOnlyComponent(def)) {
    // Passive AC panels with no declared switching map connect only like
    // conductors. L, N and PE must not collapse into one neutral-polarity bus.
    const links: IntraLink[] = [];
    for (let i = 0; i < allTerminals.length; i++) {
      const a = allTerminals[i];
      const pole = a.id.toLowerCase().split(/[-_]/).at(-1);
      if (!isAcTerminalId(a.id) || !["l", "n", "pe"].includes(pole ?? "")) continue;
      for (const b of allTerminals.slice(i + 1)) {
        if (isAcTerminalId(b.id) && b.id.toLowerCase().split(/[-_]/).at(-1) === pole) {
          links.push({ nodeId: node.id, from: a.id, to: b.id, kind: "bus" });
        }
      }
    }
    return links;
  }

  // AC line and neutral share the schema's neutral polarity. Only explicit
  // pole pairs above may connect them; generic DC grouping must not short L/N.
  const terminals = allTerminals.filter((terminal) => !isAcTerminalId(terminal.id));

  if (isFuseBlockLike(def)) {
    const links: IntraLink[] = [];
    const posInputs = terminals.filter(
      (t) => t.direction === "input" && t.polarity === "positive",
    );
    const outputs = terminals.filter((t) => t.direction === "output");
    const slotRating = (terminalId: string): number | undefined => {
      if (isFuseHolderVariant(def) && node.fuseAmp !== undefined) return node.fuseAmp ?? undefined;
      for (const c of cables) {
        if (c.fuseAmpOverride == null) continue;
        if (c.fromNodeId === node.id && c.fromTerminalId === terminalId) {
          return c.fuseAmpOverride;
        }
        if (c.toNodeId === node.id && c.toTerminalId === terminalId) {
          return c.fuseAmpOverride;
        }
      }
      return undefined;
    };
    for (const i of posInputs) {
      for (const o of outputs) {
        if (def.fuseBusGroups && !def.fuseBusGroups.some(group => group.input === i.id && group.outputs.includes(o.id))) continue;
        links.push({
          nodeId: node.id,
          from: i.id,
          to: o.id,
          kind: "fused-slot",
          fuseRating: slotRating(o.id),
        });
      }
    }
    const negs = terminals.filter(
      (t) => t.polarity === "negative" || t.polarity === "ground",
    );
    for (let i = 0; i < negs.length; i++) {
      for (let j = i + 1; j < negs.length; j++) {
        links.push({ nodeId: node.id, from: negs[i].id, to: negs[j].id, kind: "bus" });
      }
    }
    return [...permanent, ...links];
  }

  const positive: string[] = [];
  const negative: string[] = [];
  const neutral: string[] = []; // input/output without explicit polarity hint

  for (const t of terminals) {
    if (t.polarity === "positive") positive.push(t.id);
    else if (t.polarity === "negative" || t.polarity === "ground") negative.push(t.id);
    else if (t.polarity !== "signal") neutral.push(t.id);
  }

  // Neutral joins the positive group: distribution gear in this catalog only
  // sits on positive runs.
  // Signal terminals can be separate sense inputs or a relay coil. Matching
  // polarity does not make them copper; only an explicit state link above
  // can join them. Multiple cables on the same literal post still connect.
  const groups = [
    [...positive, ...neutral],
    negative,
  ].filter((g) => g.length > 1);

  const links: IntraLink[] = [];
  for (const group of groups) {
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        links.push({
          nodeId: node.id,
          from: group[i],
          to: group[j],
          kind: "bus",
        });
      }
    }
  }
  return [...permanent, ...links];
}

export type AdjacencyOptions = {
  excludeFused?: boolean;
  /** When set, only fused links with rating ≥ this threshold count as fused. */
  minFuseRating?: number;
  /** An open switch (active option with `ampOverride === 0`) drops its
   *  intra-links, so a walk can't cross it. */
  simulate?: SimulateContext;
  /**
   * Only meaningful with `simulate`. Listed nodes get synthetic intra-bus links
   * so a BFS crosses the converter as if its rails were one node. Sizing walks
   * must leave it absent, or load demand crosses the isolation barrier.
   */
  bridgeNodes?: ReadonlySet<string>;
  /**
   * Drop every cable and intra-link touching a sense terminal (`isSenseTerminal`).
   * A sense wire carries no current, so a current model must not see two
   * banks as one bus through a BMV's B+ lead; the default graph keeps them,
   * which is how `sense-terminal-as-bus` sees the bond in the first place.
   */
  excludeSense?: boolean;
};

export type Graph = {
  nodes: SystemNode[];
  cables: Cable[];
  defLookup: DefLookup;
  adj: Map<Vertex, Array<{ vertex: Vertex; via: Edge }>>;
  /** All intra-component links, regardless of filter. Used for issue lookup. */
  allIntraLinks: IntraLink[];
};

export type Edge =
  | { kind: "cable"; cableId: string; fuseRating?: number }
  | { kind: "intra"; link: IntraLink };

/** Keyed on `System` identity: the store hands out a fresh object per mutation,
 *  so a hit can't be stale. `simulate` / `bridgeNodes` have no stable key and
 *  are deliberately not cached. */
const graphCache = new WeakMap<System, WeakMap<DefLookup, Map<string, Graph>>>();

function cacheKey(opts: AdjacencyOptions): string | null {
  if (opts.simulate !== undefined) return null;
  if (opts.bridgeNodes !== undefined) return null;
  return `${opts.excludeFused ? "X" : "_"}|${opts.minFuseRating ?? ""}|${opts.excludeSense ? "S" : "_"}`;
}

export function buildGraph(
  system: System,
  defLookup: DefLookup,
  opts: AdjacencyOptions = {},
): Graph {
  const key = cacheKey(opts);
  if (key !== null) {
    let byDefinition = graphCache.get(system);
    if (!byDefinition) {
      byDefinition = new WeakMap();
      graphCache.set(system, byDefinition);
    }
    let bySystem = byDefinition.get(defLookup);
    if (!bySystem) {
      bySystem = new Map();
      byDefinition.set(defLookup, bySystem);
    }
    const cached = bySystem.get(key);
    if (cached) return cached;
    const result = buildGraphImpl(system, defLookup, opts);
    bySystem.set(key, result);
    return result;
  }
  return buildGraphImpl(system, defLookup, opts);
}

function buildGraphImpl(
  system: System,
  defLookup: DefLookup,
  opts: AdjacencyOptions = {},
): Graph {
  const adj = new Map<Vertex, Array<{ vertex: Vertex; via: Edge }>>();
  const push = (a: Vertex, b: Vertex, via: Edge) => {
    if (!adj.has(a)) adj.set(a, []);
    if (!adj.has(b)) adj.set(b, []);
    adj.get(a)!.push({ vertex: b, via });
    adj.get(b)!.push({ vertex: a, via });
  };

  const nodesById = indexNodesById(system.nodes);
  const senseTerm = (nodeId: string, termId: string): boolean => {
    const node = nodesById[nodeId];
    if (node?.kind !== "component") return false;
    const def = defLookup(node.definitionId, node.customDefinition);
    const term = def?.terminals.find((t) => t.id === termId);
    return def != null && term != null && isSenseTerminal(def, term);
  };
  for (const cable of system.cables) {
    if (
      opts.excludeSense &&
      (senseTerm(cable.fromNodeId, cable.fromTerminalId) ||
        senseTerm(cable.toNodeId, cable.toTerminalId))
    ) {
      continue;
    }
    const fuseRating = getEffectiveCableFuse(cable, nodesById, defLookup).ratingA;
    const isFused = fuseRating != null;
    if (opts.excludeFused && isFused) {
      if (
        opts.minFuseRating == null ||
        (fuseRating != null && fuseRating >= opts.minFuseRating)
      ) {
        continue;
      }
    }
    push(
      v(cable.fromNodeId, cable.fromTerminalId),
      v(cable.toNodeId, cable.toTerminalId),
      { kind: "cable", cableId: cable.id, fuseRating },
    );
  }

  const allIntraLinks: IntraLink[] = [];
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    const activeOption =
      opts.simulate != null
        ? getEffectiveStateOption(def, node.id, opts.simulate)
        : undefined;
    const links = intraLinks(node, def, system.cables, activeOption);
    // Always the full polarity-grouped set, so a per-state subset can't hide a
    // structural pairing from issue lookup.
    if (activeOption?.intraLinks) {
      allIntraLinks.push(...intraLinks(node, def, system.cables));
    } else {
      allIntraLinks.push(...links);
    }
    const openInSimulate = activeOption?.ampOverride === 0;
    // null is an explicitly emptied holder. An omitted rating in older
    // drawings means unknown, and retains the existing conductor model.
    const emptyHolder = opts.simulate && node.fuseAmp === null && isFuseHolderVariant(def);
    for (const link of links) {
      if (emptyHolder && link.kind === "fused-slot") continue;
      if (openInSimulate && link.kind !== "permanent") continue;
      if (
        opts.excludeSense &&
        (senseTerm(link.nodeId, link.from) || senseTerm(link.nodeId, link.to))
      ) {
        continue;
      }
      const isFused = link.kind === "fused-slot" || link.kind === "dc-breaker";
      if (opts.excludeFused && isFused) {
        if (
          opts.minFuseRating == null ||
          ((link.breakerRating ?? link.fuseRating) != null && (link.breakerRating ?? link.fuseRating)! >= opts.minFuseRating)
        ) {
          continue;
        }
      }
      push(v(link.nodeId, link.from), v(link.nodeId, link.to), {
        kind: "intra",
        link,
      });
    }
    if (!openInSimulate && opts.bridgeNodes?.has(node.id)) {
      const wired = def.terminals.filter((t) => t.polarity !== "signal");
      for (let i = 0; i < wired.length; i++) {
        for (let j = i + 1; j < wired.length; j++) {
          const link: IntraLink = {
            nodeId: node.id,
            from: wired[i].id,
            to: wired[j].id,
            kind: "bus",
          };
          push(v(link.nodeId, link.from), v(link.nodeId, link.to), {
            kind: "intra",
            link,
          });
        }
      }
    }
  }

  // Every chassis-ground symbol is the same electrical point, so tie them
  // together — the user needn't draw every return wire.
  const groundSymbolVerts: Array<{ vertex: Vertex; nodeId: string; from: string }> = [];
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def || def.renderVariant !== "chassis-ground") continue;
    for (const t of def.terminals) {
      groundSymbolVerts.push({ vertex: v(node.id, t.id), nodeId: node.id, from: t.id });
    }
  }
  for (let i = 0; i < groundSymbolVerts.length; i++) {
    for (let j = i + 1; j < groundSymbolVerts.length; j++) {
      const a = groundSymbolVerts[i];
      const b = groundSymbolVerts[j];
      const link: IntraLink = {
        nodeId: a.nodeId,
        from: a.from,
        to: b.from,
        kind: "bus",
      };
      allIntraLinks.push(link);
      push(a.vertex, b.vertex, { kind: "intra", link });
    }
  }

  return { nodes: system.nodes, cables: system.cables, defLookup, adj, allIntraLinks };
}

export function reachable(graph: Graph, seed: Vertex): Set<Vertex> {
  const visited = new Set<Vertex>([seed]);
  const queue: Vertex[] = [seed];
  while (queue.length) {
    const cur = queue.shift()!;
    const next = graph.adj.get(cur);
    if (!next) continue;
    for (const { vertex } of next) {
      if (!visited.has(vertex)) {
        visited.add(vertex);
        queue.push(vertex);
      }
    }
  }
  return visited;
}

export function multiSourceBfs(
  graph: Graph,
  seeds: Vertex[],
): { dist: Map<Vertex, number>; prev: Map<Vertex, Vertex | null> } {
  const dist = new Map<Vertex, number>();
  const prev = new Map<Vertex, Vertex | null>();
  const queue: Vertex[] = [];
  for (const s of seeds) {
    dist.set(s, 0);
    prev.set(s, null);
    queue.push(s);
  }
  while (queue.length) {
    const cur = queue.shift()!;
    const next = graph.adj.get(cur);
    if (!next) continue;
    for (const { vertex } of next) {
      if (!dist.has(vertex)) {
        dist.set(vertex, dist.get(cur)! + 1);
        prev.set(vertex, cur);
        queue.push(vertex);
      }
    }
  }
  return { dist, prev };
}

export function pathCables(
  graph: Graph,
  start: Vertex,
  prev: Map<Vertex, Vertex | null>,
): string[] {
  const cableIds: string[] = [];
  let cur: Vertex | null = start;
  while (cur != null) {
    const parent = prev.get(cur);
    if (!parent) break;
    const edge = graph.adj.get(cur)?.find((e) => e.vertex === parent);
    if (edge?.via.kind === "cable") cableIds.push(edge.via.cableId);
    cur = parent;
  }
  return cableIds;
}

export function findTerminals(
  system: System,
  defLookup: DefLookup,
  pred: (
    node: ComponentNode,
    def: ComponentDefinition,
    term: Terminal,
  ) => boolean,
): Vertex[] {
  const out: Vertex[] = [];
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def) continue;
    for (const t of def.terminals) {
      if (pred(n, def, t)) out.push(v(n.id, t.id));
    }
  }
  return out;
}

export const isBatteryPositive = (
  _node: ComponentNode,
  def: ComponentDefinition,
  t: Terminal,
): boolean => def.category === "battery" && t.polarity === "positive";

export const isBatteryNegative = (
  _node: ComponentNode,
  def: ComponentDefinition,
  t: Terminal,
): boolean =>
  def.category === "battery" &&
  (t.polarity === "negative" || t.polarity === "ground");

export const isLoadPositive = (
  _node: ComponentNode,
  def: ComponentDefinition,
  t: Terminal,
): boolean => def.category === "load" && !isAcTerminalId(t.id) && t.polarity === "positive";

/** Charger DC inputs with declared demand need battery-feed protection. This
 * includes DC-DC inputs as well as an inverter/charger's shared DC port. */
export const isInverterDcInputPositive = (
  _node: ComponentNode,
  def: ComponentDefinition,
  t: Terminal,
): boolean =>
  def.category === "charger" &&
  (def.ampDraw ?? 0) > 0 &&
  t.polarity === "positive" &&
  (t.direction === "input" || t.direction === "bidi");

export const isLoadNegative = (
  _node: ComponentNode,
  def: ComponentDefinition,
  t: Terminal,
): boolean =>
  def.category === "load" &&
  !isAcTerminalId(t.id) &&
  (t.polarity === "negative" || t.polarity === "ground");

/** A source's two rails stay separate: reachability is not an electrical short.
 * `sourceNodeIds` identifies the battery bank even behind cascaded converters. */
export type ActiveSourceCircuit = {
  sourceNodeIds: string[];
  converterNodeId?: string;
  positive: ReadonlySet<Vertex>;
  negative: ReadonlySet<Vertex>;
};

type LiveState = Pick<SimulateContext, "componentStates" | "loadStates">;

/** Both required DC rails must terminate on the SAME active source circuit.
 * Missing wires fail this check; omitted optional AC/signal ports do not. */
export function isDcSideSupplied(
  node: Pick<ComponentNode, "id">,
  def: ComponentDefinition,
  side: "input" | "output" | "bidi",
  circuits: readonly ActiveSourceCircuit[],
): boolean {
  return dcPortTerminalGroups(def, side).some((terms) => {
    const positives = terms.filter((t) => t.polarity === "positive");
    const negatives = terms.filter((t) => t.polarity === "negative" || t.polarity === "ground");
    return positives.length > 0 && negatives.length > 0 && circuits.some((source) =>
      positives.every((t) => source.positive.has(v(node.id, t.id))) &&
      negatives.every((t) => source.negative.has(v(node.id, t.id))),
    );
  });
}

function railReach(graph: Graph, starts: readonly Vertex[]): Set<Vertex> {
  const result = new Set<Vertex>();
  for (const start of starts) for (const vert of reachable(graph, start)) result.add(vert);
  return result;
}

function converterOn(node: ComponentNode, def: ComponentDefinition, simulate?: LiveState): boolean {
  if (!simulate) return true;
  const state = getEffectiveStateOption(def, node.id, simulate);
  if (state?.role !== "draw" && configuredChargeCurrent(node, def) === 0) return false;
  return state?.id !== "off" && state?.ampOverride !== 0 && state?.ampMultiplier !== 0;
}

export type ChargerEngineDetection = "off" | "forced" | "running" | "waiting" | "unresolved";

/** Steady-state approximation for reviewed Orion XS/Orion-Tr Smart charger models. Victron
 * manual §§4.1 / 4.4.6 detects engine shutdown from input voltage and delays;
 * our nominal-voltage model cannot reproduce those thresholds. Instead, Auto
 * requires a running alternator on BOTH physical input rails. Unknown topology
 * stays unresolved, never a battery-only assumption of a running engine.
 * Forced represents detection disabled, not the distinct remote L-pin signal
 * or power-supply mode. Voltage lock-out and transient timing are not modeled.
 * Static design sizing never uses this live gate. */
export function chargerEngineDetection(
  node: ComponentNode,
  def: ComponentDefinition,
  system: System,
  lookup: DefLookup,
  simulate: LiveState | undefined,
  graph: Graph = buildGraph(system, lookup, { simulate, excludeSense: true }),
): ChargerEngineDetection | undefined {
  if (!simulate || !hasEngineShutdownDetection(def)) return undefined;
  if (!converterOn(node, def, simulate)) return "off";
  if (getEffectiveStateOption(def, node.id, simulate)?.id === "forced") return "forced";
  let connected = false;
  for (const source of system.nodes) {
    if (source.kind !== "component") continue;
    const sourceDef = lookup(source.definitionId, source.customDefinition);
    // Use declared engine/alternator identity; a generic DC supply or an
    // unrelated charging source cannot prove that the engine is running.
    if (!sourceDef || !(sourceDef.category === "engine" || sourceDef.id.startsWith("src-alternator"))) continue;
    for (const port of dcChargingPortGroups(sourceDef)) {
      const pos = port.filter(t => t.polarity === "positive").map(t => v(source.id, t.id));
      const neg = port.filter(t => t.polarity === "negative" || t.polarity === "ground").map(t => v(source.id, t.id));
      if (!pos.length || !neg.length) continue;
      const positive = railReach(graph, pos), negative = railReach(graph, neg);
      if ([...positive].some(vertex => negative.has(vertex)) ||
          !isDcSideSupplied(node, def, "input", [{ sourceNodeIds: [source.id], positive, negative }])) continue;
      connected = true;
      const amps = applyStateToAmp(sourceDef.chargeAmpsTypical ?? sourceDef.ampOutput,
        getEffectiveStateOption(sourceDef, source.id, simulate), "output") ?? 0;
      if (amps > 0) return "running";
    }
  }
  return connected ? "waiting" : "unresolved";
}

export function engineDetectionAllowsCharging(status: ChargerEngineDetection | undefined): boolean {
  return status == null || status === "running" || status === "forced";
}

/** Real source circuits only. A converter receives power on its input and
 * creates a separate output rail; its battery-connected output never powers
 * its input backwards. Static cable sizing does not use this live gate. */
export function computeActiveSourceCircuits(
  system: System,
  defLookup: DefLookup,
  simulate?: LiveState,
): ActiveSourceCircuit[] {
  const graph = buildGraph(system, defLookup, { simulate, excludeSense: true });
  const sources: ActiveSourceCircuit[] = [];
  for (const range of computeBatterySourceRanges(system, defLookup, graph, simulate)) {
    sources.push({
      sourceNodeIds: range.batteryNodeIds,
      positive: railReach(graph, range.topPositives),
      negative: railReach(graph, range.bottomNegatives),
    });
  }
  const converters = system.nodes.flatMap((node) => {
    if (node.kind !== "component") return [];
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def || (def.category !== "charger" && def.category !== "load") || !converterOn(node, def, simulate) ||
        !engineDetectionAllowsCharging(chargerEngineDetection(node, def, system, defLookup, simulate, graph))) return [];
    return [{ node, def }];
  });
  const supplied = new Set<string>();
  for (let pass = 0; pass < converters.length; pass++) {
    let changed = false;
    for (const { node, def } of converters) {
      if (supplied.has(node.id)) continue;
      const inputSources = sources.filter((source) => isDcSideSupplied(node, def, "input", [source]));
      if (inputSources.length === 0) continue;
      for (const output of dcPortTerminalGroups(def, "output")) {
        const pos = output.filter((t) => t.polarity === "positive").map((t) => v(node.id, t.id));
        const neg = output.filter((t) => t.polarity !== "positive").map((t) => v(node.id, t.id));
        if (pos.length === 0 || neg.length === 0) continue;
        const positive = railReach(graph, pos), negative = railReach(graph, neg);
        if ([...positive].some((vertex) => negative.has(vertex))) continue;
        sources.push({
          sourceNodeIds: [...new Set(inputSources.flatMap((source) => source.sourceNodeIds))],
          converterNodeId: node.id, positive, negative,
        });
      }
      supplied.add(node.id);
      changed = true;
    }
    if (!changed) break;
  }
  return sources;
}

/** Live terminal visibility with complete source-circuit provenance. Negative
 * rails may remain bonded on a dead load, but its positive is not powered. */
export function computeEnergizedTerminals(
  system: System,
  defLookup: DefLookup,
  simulate: LiveState | undefined,
): Set<string> {
  const graph = buildGraph(system, defLookup, { simulate, excludeSense: true });
  const sources = computeActiveSourceCircuits(system, defLookup, simulate);
  const energized = new Set<Vertex>();
  for (const source of sources) {
    for (const vert of source.positive) energized.add(vert);
    for (const vert of source.negative) energized.add(vert);
  }
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    if (def.category === "load" && !isDcSideSupplied(node, def, "input", sources)) {
      for (const term of def.terminals) if (term.polarity === "positive") energized.delete(v(node.id, term.id));
    }
    if (!converterOn(node, def, simulate)) continue;
    // An inverter can energize its AC outputs without declaring AC a DC rail.
    if ((def.category === "load" || def.category === "charger") && isDcSideSupplied(node, def, "input", sources)) {
      for (const term of def.terminals.filter((t) => t.direction === "output" && isAcTerminalId(t.id))) {
        for (const vert of reachable(graph, v(node.id, term.id))) energized.add(vert);
      }
    }
  }
  for (const path of activeSolarPaths(system, defLookup, graph, sources, simulate)) {
    for (const vertex of path.terminals) energized.add(vertex);
  }
  return energized;
}

export type ActiveSolarPath = {
  controllerNodeId: string;
  panelNodeIds: ReadonlySet<string>;
  terminals: ReadonlySet<Vertex>;
};

/** Keep generating panels associated with the controller whose complete input
 * circuit they feed. Sharing only a PV return never joins two solar arrays. */
export function computeActiveSolarPaths(
  system: System,
  defLookup: DefLookup,
  simulate?: LiveState,
): ActiveSolarPath[] {
  const graph = buildGraph(system, defLookup, { simulate, excludeSense: true });
  return activeSolarPaths(system, defLookup, graph, computeActiveSourceCircuits(system, defLookup, simulate), simulate);
}

export type ConnectedSolarInput = ActiveSolarPath & {
  positiveTerminals: ReadonlySet<Vertex>;
  negativeTerminals: ReadonlySet<Vertex>;
};

/** Physical input completeness for wiring diagnostics. Switch positions and
 * battery-side charging availability do not change whether the PV wiring is
 * complete. Keep this separate from live generation/energization. */
export function computeConnectedSolarInputs(system: System, defLookup: DefLookup): ConnectedSolarInput[] {
  return solarInputPaths(system, defLookup, buildGraph(system, defLookup, { excludeSense: true }), [], undefined, false);
}

function activeSolarPaths(
  system: System,
  defLookup: DefLookup,
  graph: Graph,
  sources: readonly ActiveSourceCircuit[],
  simulate?: LiveState,
): ActiveSolarPath[] {
  const controllers = new Map<string, { controllerNodeId: string; panelNodeIds: Set<string>; terminals: Set<Vertex> }>();
  for (const path of solarInputPaths(system, defLookup, graph, sources, simulate)) {
    const combined = controllers.get(path.controllerNodeId) ?? { controllerNodeId: path.controllerNodeId, panelNodeIds: new Set(), terminals: new Set() };
    for (const id of path.panelNodeIds) combined.panelNodeIds.add(id);
    for (const vertex of path.terminals) combined.terminals.add(vertex);
    controllers.set(path.controllerNodeId, combined);
  }
  return [...controllers.values()];
}

/** Identify complete PV strings as directed generating paths, rather than
 * shorting every panel's terminals into the conductor graph. A series panel
 * connects its positive to its negative for this availability walk only.
 * Intersecting forward and reverse reach excludes dangling string branches. */
function solarInputPaths(
  system: System,
  defLookup: DefLookup,
  graph: Graph,
  sources: readonly ActiveSourceCircuit[],
  simulate?: LiveState,
  requireOutputSupply = true,
): ConnectedSolarInput[] {
  const forward = new Map<Vertex, Set<Vertex>>();
  const reverse = new Map<Vertex, Set<Vertex>>();
  const edge = (a: Vertex, b: Vertex) => {
    (forward.get(a) ?? forward.set(a, new Set()).get(a)!).add(b);
    (reverse.get(b) ?? reverse.set(b, new Set()).get(b)!).add(a);
  };
  for (const [a, neighbors] of graph.adj) for (const { vertex: b } of neighbors) edge(a, b);
  const panels: Array<{ nodeId: string; positive: Vertex[]; negative: Vertex[] }> = [];
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def || def.category !== "solar" || !converterOn(node, def, simulate)) continue;
    const pos = def.terminals.filter((t) => t.polarity === "positive");
    const neg = def.terminals.filter((t) => t.polarity === "negative");
    panels.push({ nodeId: node.id, positive: pos.map((t) => v(node.id, t.id)), negative: neg.map((t) => v(node.id, t.id)) });
    for (const p of pos) for (const n of neg) edge(v(node.id, p.id), v(node.id, n.id));
  }
  const walk = (adj: Map<Vertex, Set<Vertex>>, seeds: Vertex[]): Set<Vertex> => {
    const seen = new Set(seeds);
    const queue = [...seeds];
    for (let i = 0; i < queue.length; i++) for (const next of adj.get(queue[i]) ?? []) {
      if (seen.has(next)) continue;
      seen.add(next);
      queue.push(next);
    }
    return seen;
  };
  // An edge whose negative can reach its positive is shorted or part of an
  // opposing source loop. Such an edge cannot deliver the panel's rated watts.
  const generating = panels.filter((panel) => panel.positive.length > 0 && panel.negative.length > 0 &&
    !panel.positive.some((positive) => walk(forward, panel.negative).has(positive)),
  );
  const paths: ConnectedSolarInput[] = [];
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def || def.category !== "charger" || !converterOn(node, def, simulate)) continue;
    // A controller's auxiliary LOAD ports may be unused. Solar charging needs
    // the BATT pair, not a return through every independently controlled output.
    const batteryOutput = def.terminals.filter((t) => t.direction === "output" && /^batt[-_]/i.test(t.id));
    const batterySide = batteryOutput.length > 0
      ? { ...def, terminals: [...batteryOutput, ...def.terminals.filter((t) => t.direction === "bidi")] }
      : def;
    if (requireOutputSupply && !isDcSideSupplied(node, batterySide, "output", sources)) continue;
    for (const pv of pvInputTerminalGroups(def)) {
      const pos = pv.filter(t => t.polarity === "positive").map(t => v(node.id, t.id));
      const neg = pv.filter(t => t.polarity === "negative").map(t => v(node.id, t.id));
      if (pos.length === 0 || neg.length === 0) continue;
      const fromPositive = walk(forward, pos);
      const toNegative = walk(reverse, neg);
      const panelNodeIds = new Set(generating.filter((panel) =>
        panel.positive.every((vertex) => fromPositive.has(vertex)) &&
        panel.negative.every((vertex) => toNegative.has(vertex)),
      ).map((panel) => panel.nodeId));
      if (panelNodeIds.size === 0) continue;
      const active = new Set<Vertex>();
      for (const vertex of fromPositive) if (toNegative.has(vertex)) active.add(vertex);
      // Copper may reach a dangling or disabled panel. Its generating edge must
      // belong to this controller's path before either terminal counts as live.
      for (const panel of system.nodes) {
        if (panel.kind !== "component" || panelNodeIds.has(panel.id)) continue;
        const panelDef = defLookup(panel.definitionId, panel.customDefinition);
        if (panelDef?.category !== "solar") continue;
        for (const terminal of panelDef.terminals) active.delete(v(panel.id, terminal.id));
      }
      paths.push({ controllerNodeId: node.id, panelNodeIds, terminals: active, positiveTerminals: new Set(pos), negativeTerminals: new Set(neg) });
    }
  }
  return paths;
}
